import { apiGet, apiPost, unwrap } from './apiClient';
import type {
  ResponseDTO,
  PaginationMeta,
  DeviceDetailDTO,
  DeviceSummaryDTO,
  DeviceAppsSummaryDTO,
  DevicePolicySummaryDTO,
  MdmCertificateInventory,
  DeviceCheckInRequest,
  RegisterDeviceRequest,
  BulkDeviceLockRequest,
  BulkDeviceUnenrollRequest,
} from '@/types/api';

const BASE = '/api/v1/devices';

export async function listDevices(page = 0, size = 20) {
  const res = await apiGet<ResponseDTO<DeviceSummaryDTO[]>>(
    `${BASE}/`,
    { params: { page, size }, includeTenantHeader: true }
  );
  return res;
}

export async function getDeviceDetail(uuid: string) {
  const res = await apiGet<ResponseDTO<DeviceDetailDTO>>(
    `${BASE}/data/${uuid}`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function getDeviceSummary(uuid: string) {
  const res = await apiGet<ResponseDTO<DeviceSummaryDTO>>(
    `${BASE}/uuid/${uuid}`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function getDeviceBySerial(serialNumber: string) {
  const res = await apiGet<ResponseDTO<unknown>>(
    `${BASE}/serial/${encodeURIComponent(serialNumber)}`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function getDeviceApps(deviceId: string, page = 0, size = 20) {
  const res = await apiGet<ResponseDTO<DeviceAppsSummaryDTO[]>>(
    `${BASE}/${deviceId}/applications`,
    { params: { page, size }, includeTenantHeader: true }
  );
  return res;
}

export async function getDeviceCerts(deviceId: string) {
  const res = await apiGet<ResponseDTO<MdmCertificateInventory[]>>(
    `${BASE}/${deviceId}/certificates`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function getDeviceManagedApps(deviceId: string, page = 0, size = 20) {
  const res = await apiGet<ResponseDTO<DeviceAppsSummaryDTO[]>>(
    `${BASE}/${deviceId}/managed-apps`,
    { params: { page, size }, includeTenantHeader: true }
  );
  return res;
}

export async function getDevicePolicies(deviceId: string, page = 0, size = 20) {
  const res = await apiGet<ResponseDTO<DevicePolicySummaryDTO[]>>(
    `${BASE}/${deviceId}/policies`,
    { params: { page, size }, includeTenantHeader: true }
  );
  return res;
}

export async function checkInDevice(body: DeviceCheckInRequest) {
  const res = await apiPost<ResponseDTO<string>>(
    `${BASE}/check-in`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function inviteUser(body: RegisterDeviceRequest) {
  const res = await apiPost<ResponseDTO<string>>(
    `${BASE}/invite-user`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function bulkLock(body: BulkDeviceLockRequest) {
  const res = await apiPost<ResponseDTO<Record<string, string>>>(
    `${BASE}/bulk-lock`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function bulkUnlock(body: { deviceIds: string[] }) {
  const res = await apiPost<ResponseDTO<Record<string, string>>>(
    `${BASE}/bulk-unlock`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function bulkUnenroll(body: BulkDeviceUnenrollRequest) {
  const res = await apiPost<ResponseDTO<Record<string, string>>>(
    `${BASE}/bulk-unenroll`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}
